import { useEffect, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { fetchDeliveryMethods } from '../../../common/fetch.js';
import { ensureShoppingBasketId, parseBasketJson, useShoppingBasket } from '../../../common/hooks/useShoppingBasket.js';
import { useVoucher } from '../../../common/hooks/useVoucher';
import { Loading } from '../../../components/Loading/Loading.js';
import { LastItemRemovalDialog } from '../../../components/PlaceOrder/LastItemRemovalDialog.js';
import { PlaceOrder } from '../../../components/PlaceOrder/PlaceOrder.js';
import { useModelsForBasket } from '../../../components/PlaceOrder/hooks/useModelsForBasket';
import {
  handleAddOrRemoveItemForPlaceOrder,
  handleAddonChangeForPlaceOrder,
} from '../../../components/PlaceOrder/utils/placeOrderAnalyticsUtils';
import { getProductDeliveryTypes, mergeDeliveryMethods } from '../../../components/PlaceOrder/utils/placeOrderUtils.js';
import { DialogContainer } from '../../../components/SelfService/selfServiceDialogs.js';
import {
  changeQuantityInBasket,
  findBasketItemForId,
  isItemDiscontinued,
  replaceAddonsForBasketItem,
} from '../../../components/ShoppingBasket/shoppingBasketUtils';
import { useAuth } from '../AuthProvider.js';
import { useOnlineModelsContext } from '../onlineModel/onlineModelContextProvider.js';
import {
  areAllModelCodesLoaded,
  expandRelatedModelCodes,
  getOnlineModelsWithVoucher,
} from '../onlineModel/onlineModelService.js';
import type { PlaceOrderLoaderData } from '../../../common/loaders';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasket.js';
import type { CampaignData } from '../../../components/PlaceOrder/placeOrderTypes';
import type { MergedDeliveryMethod } from '../../../components/PlaceOrder/utils/placeOrderUtils.js';

export const PlaceOrderPath = () => {
  const { isAuthenticated, isLoading, authenticatedUser } = useAuth();
  const { shoppingBasket, setShoppingBasket } = useShoppingBasket();
  const navigate = useNavigate();
  const { priceAlteredModels, fetchModelsByCodes, activeVoucherCode, setActiveVoucher, loadVoucherModels } =
    useOnlineModelsContext();

  const [deliveryMethods, setDeliveryMethods] = useState<MergedDeliveryMethod[] | undefined>();
  const [isLastItemModalVisible, setIsLastItemModalVisible] = useState(false);
  const [pendingRemovalId, setPendingRemovalId] = useState<string | undefined>();
  const [basketIdReady, setBasketIdReady] = useState(false);
  const shoppingCartJson: ShoppingBasketType = parseBasketJson(shoppingBasket);
  const { companyInfo, mdmId, expiringEppSubscriptions } = useLoaderData() as PlaceOrderLoaderData;

  const onlineModelCodeGuids = [...new Set(shoppingCartJson.items?.map(cartItem => cartItem.guid) ?? [])];
  const expandedGuids = expandRelatedModelCodes(onlineModelCodeGuids);
  const privateOnlineModels = useModelsForBasket(shoppingCartJson);
  const basketVoucherCode = shoppingCartJson.vouchers?.[0];

  // Restore voucher from basket on page reload
  const expandedGuidsKey = expandedGuids.join(',');
  useEffect(() => {
    if (basketVoucherCode && basketVoucherCode !== activeVoucherCode) {
      setActiveVoucher(basketVoucherCode);
      loadVoucherModels(basketVoucherCode, expandedGuidsKey.split(','));
    }
  }, [basketVoucherCode, activeVoucherCode, setActiveVoucher, loadVoucherModels, expandedGuidsKey]);

  const effectiveVoucherCode = basketVoucherCode || activeVoucherCode;

  const voucherOnlineModels = effectiveVoucherCode
    ? getOnlineModelsWithVoucher(expandedGuids, effectiveVoucherCode, priceAlteredModels.onlineModelsByVoucher)
    : undefined;

  const voucherHook = useVoucher(
    loadVoucherModels,
    setActiveVoucher,
    fetchModelsByCodes,
    shoppingCartJson,
    effectiveVoucherCode,
    voucherOnlineModels
  );

  const { campaignsFromVoucher, campaignContextsFromVoucher } = voucherHook;

  const basketItemCount = shoppingCartJson.items?.length ?? 0;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Redirect if not authenticated
    if (!isAuthenticated) {
      navigate(paths.SHOPPING_BASKET, { state: { from: 'kassa' }, replace: true });
      return;
    }

    // Redirect if basket is empty (basket is cleared before navigation, so this won't trigger during order submission)
    if (basketItemCount === 0) {
      navigate(paths.SHOPPING_BASKET, { state: { from: 'kassa' }, replace: true });
      return;
    }

    // Ensure shopping basket ID exists
    ensureShoppingBasketId().then(basketIdOk => {
      if (!basketIdOk) {
        navigate(paths.SHOPPING_BASKET);
        return;
      }
      setBasketIdReady(true);
    });
  }, [isLoading, isAuthenticated, navigate, basketItemCount]);

  const onlineModels = voucherOnlineModels?.length ? voucherOnlineModels : privateOnlineModels;

  // Fetch delivery methods with correct productType (e.g. EPP) for 0€ delivery pricing.
  // Re-fetches when basket items or models change.
  const modelsLoaded = onlineModels.length > 0 && onlineModels.length >= onlineModelCodeGuids.length;
  const productTypesKey = modelsLoaded
    ? getProductDeliveryTypes(shoppingCartJson.items ?? [], onlineModels).join(',')
    : undefined;
  useEffect(() => {
    if (productTypesKey === undefined) {
      return;
    }
    fetchDeliveryMethods(productTypesKey ? productTypesKey.split(',') : undefined).then(response => {
      setDeliveryMethods(mergeDeliveryMethods(response));
    });
  }, [productTypesKey]);

  const modelsFoundForCheckout =
    expandedGuids.length > onlineModelCodeGuids.length
      ? areAllModelCodesLoaded(expandedGuids, onlineModels)
      : onlineModelCodeGuids.length === 0 ||
        onlineModelCodeGuids.some(guid => onlineModels.find(model => model.onlineModelCode === guid));

  if (isLoading || !modelsFoundForCheckout || !basketIdReady) {
    return <Loading big={true} />;
  }

  // Redirect to shopping basket if all items are discontinued — the basket page shows the DiscontinuedProducts UI
  const availableItemCount =
    shoppingCartJson.items?.filter(item => !isItemDiscontinued(item, onlineModels)).length ?? 0;
  if (onlineModels.length > 0 && availableItemCount === 0) {
    navigate(paths.SHOPPING_BASKET, { state: { from: 'kassa' }, replace: true });
    return null;
  }

  if (!isAuthenticated || !authenticatedUser || !companyInfo) {
    return null;
  }

  const changeQuantity = (basketItemId: string, quantity: number) => {
    const basketItem = findBasketItemForId(shoppingCartJson.items, basketItemId);

    // Show confirmation dialog if removing this item would empty the cart of available (non-discontinued) items
    const isLastAvailableItem = availableItemCount === 1;
    const wouldEmptyCart = isLastAvailableItem && quantity === 0;

    if (wouldEmptyCart) {
      setPendingRemovalId(basketItemId);
      setIsLastItemModalVisible(true);
      return;
    }

    handleAddOrRemoveItemForPlaceOrder(basketItem, quantity, onlineModels);
    setShoppingBasket(JSON.stringify(changeQuantityInBasket(shoppingCartJson, basketItemId, quantity)));
  };

  const removeLastCartItem = () => {
    if (pendingRemovalId) {
      const basketItem = findBasketItemForId(shoppingCartJson.items, pendingRemovalId);
      handleAddOrRemoveItemForPlaceOrder(basketItem, 0, onlineModels);
      setShoppingBasket(JSON.stringify(changeQuantityInBasket(shoppingCartJson, pendingRemovalId, 0)));
      setIsLastItemModalVisible(false);
      setPendingRemovalId(undefined);
      navigate(paths.WEB_SHOP);
    }
  };

  const updateAddons = (
    basketItemId: string,
    productIndex: number,
    addons: string[],
    addOnAttributes?: { [addOnCode: string]: { [key: string]: string } }
  ) => {
    const basketItem = findBasketItemForId(shoppingCartJson.items, basketItemId);
    handleAddonChangeForPlaceOrder(basketItem, productIndex, addons, onlineModels);
    setShoppingBasket(
      JSON.stringify(replaceAddonsForBasketItem(shoppingCartJson, basketItemId, productIndex, addons, addOnAttributes))
    );
  };

  return (
    <>
      <PlaceOrder
        shoppingBasket={shoppingCartJson}
        onlineModels={onlineModels}
        shoppingBasketUpdateMethods={{ changeQuantity, updateAddons }}
        user={authenticatedUser}
        deliveryMethods={deliveryMethods}
        companyInfo={companyInfo}
        mdmId={mdmId}
        campaignData={
          effectiveVoucherCode
            ? ({ voucherCode: effectiveVoucherCode, campaignsFromVoucher, campaignContextsFromVoucher } as CampaignData)
            : undefined
        }
        expiringEppSubscriptions={expiringEppSubscriptions}
      />
      <LastItemRemovalDialog
        isVisible={isLastItemModalVisible}
        onCancel={() => {
          setIsLastItemModalVisible(false);
          setPendingRemovalId(undefined);
        }}
        onConfirm={removeLastCartItem}
      />
      {/* // Needed for DialogType.ORDER_SIM_CARD_NUMBER_HELP */}
      <DialogContainer />
    </>
  );
};
